//#region IMPORTS
import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  isAnyOf,
} from "@reduxjs/toolkit";
import { Country, CountryState } from "../../models/country";
import { RootState } from "../../store/configureStore";
import { toast } from "react-toastify";
import agent from "../../api/agent";
//#endregion

//#region INIT
// Set initial state
const initialExtraState: CountryState = {
  countries_loaded: false,
  metaData: null,
  loading: false,
  status: "idle",
};
//#endregion

//#region CREATE ADPATER
const countriesAdapter = createEntityAdapter<Country>();
//#endregion

//#region READ
// Fetch All
export const fetchCountriesAsync = createAsyncThunk<
  Country[],
  void,
  { state: RootState }
>("countries/fetchCountriesAsync", async (_, thunkAPI) => {
  try {
    const { data, meta } = await agent.CountryManagement.list();
    thunkAPI.dispatch(setCountriesMetaData(meta));
    return data;
  } catch (error: any) {
    const raw = error?.response?.data?.message ?? error?.message;
    const message =
      typeof raw === "string"
        ? raw
        : error?.response?.status === 403
          ? "You don't have permission to view countries."
          : "Something went wrong while loading countries.";
    return thunkAPI.rejectWithValue(message);
  }
});

// Fetch by id
export const fetchCountryAsync = createAsyncThunk<
  Country,
  number,
  { state: RootState }
>("countries/fetchCountryAsync", async (id, thunkAPI) => {
  try {
    const { data } = await agent.CountryManagement.detail(id);
    return data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});
//#endregion

//#region CREATE REDUCER SLICE
export const countrySlice = createSlice({
  name: "countries",
  initialState:
    countriesAdapter.getInitialState<CountryState>(initialExtraState),
  reducers: {
    setCountriesPageNumber: (state, action) => {
      state.countries_loaded = false;
      state.loading = true;
    },
    setCountriesMetaData: (state, action) => {
      state.metaData = action.payload;
    },
    clearCountries: () =>
      countriesAdapter.getInitialState<CountryState>(initialExtraState),
    setCountry: (state, action) =>
      countriesAdapter.upsertOne(state, action.payload),
    removeCountry: (state, action) =>
      countriesAdapter.removeOne(state, action.payload),
  },
  extraReducers: (builder) => {
    // FETCH countries
    builder.addCase(fetchCountriesAsync.pending, (state) => {
      state.status = "pendingFetchCountries";
      state.loading = true;
    });
    builder.addCase(fetchCountriesAsync.fulfilled, (state, action) => {
      countriesAdapter.setAll(state, action.payload);
      state.countries_loaded = true;
      state.loading = false;
      state.status = "idle";
    });

    // FETCH country by id
    builder.addCase(fetchCountryAsync.pending, (state) => {
      state.loading = true;
      state.status = "pendingFetchCountry";
    });
    builder.addCase(fetchCountryAsync.fulfilled, (state, { payload }) => {
      countriesAdapter.upsertOne(state, payload);
      state.loading = false;
      state.status = "idle";
    });

    //#region CRUD REJECTION
    builder.addMatcher(
      isAnyOf(fetchCountryAsync.rejected, fetchCountriesAsync.rejected),
      (state, { payload }) => {
        toast.error(`${payload}`);
        state.loading = false;
        state.status = "idle";
      }
    );
    //#endregion
  },
});
//#endregion

//#region NORMALISED STATE
export const countrySelectors = countriesAdapter.getSelectors(
  (state: RootState) => state.countriesManagement
);
//#endregion

//#region EXPORT
export const {
  setCountriesPageNumber,
  setCountriesMetaData,
  removeCountry,
  clearCountries,
  setCountry,
} = countrySlice.actions;
//#endregion
